import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { CategoriesListItemOverride, CategoryPricingType, SalesInputItem } from "../../../../../graphql/types"
import { ItemIsActiveFilter, ItemsAndCategoriesTableFilters } from "../../types"
import { computeOverriddenValue } from "../../utils"
import { prop } from "ramda"
import type { CategoriesTableDataItem } from "./CategoriesTable"

export const getPricingTypeLabel = (type: Maybe<CategoryPricingType> | string): Maybe<string> => {
    switch (type) {
        case "fixed":
            return "Fixed Price"

        case "setUponSale":
            return "Set Upon Sale"

        case "setInOptions":
            return "Set In Options"

        case "unitBased":
            return "Unit Based"

        case "catering":
            return "Catering"

        default:
            return null
    }
}

export const isAnyChildrenActiveFilterValue = (
    categoryChildren: CategoriesTableDataItem[],
    filters: ItemsAndCategoriesTableFilters,
    selectedLocationId: Maybe<string>,
    overrides: CategoriesListItemOverride[]
): boolean => {
    if (!selectedLocationId || filters.isActive === ItemIsActiveFilter.All) {
        return false
    }

    return categoryChildren.some(c => {
        const isActive = computeOverriddenValue(overrides, selectedLocationId, c.id, prop("isActive"))
        const isActiveNested = c.children?.length
            ? isAnyChildrenActiveFilterValue(c.children ?? [], filters, selectedLocationId, overrides)
            : false
        return (
            (filters.isActive === ItemIsActiveFilter.Active && (isActive || isActiveNested)) ||
            (filters.isActive === ItemIsActiveFilter.Inactive && (!isActive || !isActiveNested))
        )
    })
}

export const getInitialExpandedRowsIds = (
    data: CategoriesTableDataItem[] | SalesInputItem[]
): Record<string, boolean> => {
    const initialExpandedRowsIds: Record<string, boolean> = {}
    data.forEach(item => {
        if (item.children?.length > 0) {
            initialExpandedRowsIds[item.id] = true

            const childExpandedRowsIds = getInitialExpandedRowsIds(item.children)
            Object.keys(childExpandedRowsIds).forEach(childId => {
                initialExpandedRowsIds[childId] = true
            })
        }
    })
    return initialExpandedRowsIds
}

export const handleCategoriesListItemOverridesInsert = (
    currentOverrides: CategoriesListItemOverride[],
    payloadOverrides: CategoriesListItemOverride[],
    locationId: Maybe<string>
): CategoriesListItemOverride[] => {
    // Fallback for schematized view action, remove after refactored
    const payloadOverrideLocationId = locationId || payloadOverrides[0]?.locationId

    // If there are already overrides for this location, do not add again
    // We could consider updating existing overrides here in the future if needed, but it is all updated by local redux actions per item
    const overridesExists = currentOverrides.find(o => o.locationId === payloadOverrideLocationId)
    if (overridesExists) {
        return currentOverrides
    }

    return [...currentOverrides, ...payloadOverrides]
}
